/**
 * The channel icon on a conversation row's avatar. Amir 2026-09-12, finding 5:
 * *"the green dot becomes a channel icon, bottom-right of the avatar, showing
 * WhatsApp, Instagram or email."*
 *
 * ┌─ ⛔ IT CLOSES `colour-is-the-only-carrier` BY CONSTRUCTION RATHER THAN BY ──┐
 * │   ADDITION, WHICH IS THE WHOLE REASON IT IS BETTER THAN THE DOT.            │
 * │                                                                            │
 * │ The dot carried colour, and the fix for that was a per-channel SHAPE plus a │
 * │ `title` plus a visually-hidden name - three carriers bolted on to a control │
 * │ whose native carrier was one. **An icon differs by its geometry before      │
 * │ anything is added to it**, so a reader who cannot separate the green from   │
 * │ the red still sees an envelope against a camera.                           │
 * │                                                                            │
 * │ ⚠️ AND THE OLD CARRIERS ARE KEPT, NOT REPLACED. The colour, the `title` and │
 * │ the hidden name all stay: a 14px glyph is legible to somebody who knows     │
 * │ what they are looking for and not to somebody meeting it for the first      │
 * │ time, and removing a working carrier to add a better one is a net loss to   │
 * │ whoever relied on the old one.                                             │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ THESE ARE CATEGORY GLYPHS, NOT BRAND MARKS, AND THAT IS STATED RATHER THAN
 * LEFT TO BE NOTICED. A bubble, a camera outline, a lightning bubble and an
 * envelope. **The pixel-accurate WhatsApp and Instagram logos are a different
 * decision** - they are trademarks with usage rules, and reproducing one from
 * memory produces a mark that is subtly wrong everywhere it appears. Whether
 * this product ships brand marks is VISUAL-DESIGN's, and the shape of this
 * component does not change if it does: one `<svg>` per channel, same size, same
 * accessible name.
 *
 * ⛔ `currentColor` THROUGHOUT AND NOT ONE COLOUR LITERAL. `ops/check-colors.sh`
 * forbids them, and the badge's own comment already recorded why brand hexes
 * were rejected for the dot. The glyph inherits the badge's foreground token, so
 * VISUAL-DESIGN retunes it by changing a token rather than by editing paths.
 */
import type React from 'react'

/**
 * ⚠️ ONE ENTRY PER PERMITTED CHANNEL, NOT PER CHANNEL WE HAVE BUILT. The schema
 * has permitted `whatsapp | email | instagram | messenger` since FOUNDATION, so
 * the database can already produce a value this file has never seen - and the
 * `default` arm below is what stops that being an empty badge.
 */
function glyphFor(channel: string): React.JSX.Element {
  switch (channel) {
    /**
     * A speech bubble with a tail. ⚠️ The handset that makes the real WhatsApp
     * mark recognisable is deliberately absent: drawn from memory it comes out
     * wrong, and a wrong handset is worse than no handset.
     */
    case 'whatsapp':
      return (
        <path
          d="M8 2.2a5.8 5.8 0 0 0-5 8.75L2.2 13.8l2.95-.8A5.8 5.8 0 1 0 8 2.2Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      )
    /**
     * The camera outline, which IS Instagram's geometry: a rounded square, a
     * circle inside it, and a dot in the upper right. Constructible exactly,
     * unlike the two above.
     */
    case 'instagram':
      return (
        <>
          <rect
            x="2.4"
            y="2.4"
            width="11.2"
            height="11.2"
            rx="3.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <circle cx="8" cy="8" r="2.9" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="11.6" cy="4.4" r="0.95" fill="currentColor" />
        </>
      )
    /** A bubble with the bolt through it. Same caveat as WhatsApp's. */
    case 'messenger':
      return (
        <>
          <path
            d="M8 2.2c-3.2 0-5.8 2.4-5.8 5.4 0 1.7.85 3.2 2.2 4.2v2.2l2-1.1c.5.14 1.05.2 1.6.2 3.2 0 5.8-2.4 5.8-5.5S11.2 2.2 8 2.2Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path
            d="m4.9 9.5 2.3-2.4 1.5 1.5 2.4-2.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      )
    /**
     * ⛔ AN ENVELOPE, AND IT IS THE ONE CHANNEL WHOSE ICON IS NOT A BRAND
     * QUESTION AT ALL. Email is a medium rather than a company, so the glyph is
     * simply the thing.
     */
    case 'email':
      return (
        <>
          <rect
            x="1.8"
            y="3.6"
            width="12.4"
            height="8.8"
            rx="1.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="m2.4 4.8 5.6 4 5.6-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      )
    /**
     * ⛔ A CHANNEL THIS BUILD HAS NOT HEARD OF GETS A FILLED DISC RATHER THAN
     * NOTHING. The old badge's failure mode for an unknown channel was an
     * invisible badge - a rule that did not exist produced no background - and
     * an absent icon would be the same defect in a new mechanism. `channelName`
     * one file over already returns the raw value for the same reason.
     */
    default:
      return <circle cx="8" cy="8" r="4.6" fill="currentColor" />
  }
}

export function ChannelIcon({ channel }: { channel: string }): React.JSX.Element {
  return (
    /** ⚠️ `aria-hidden`: the badge already carries the channel's NAME as text for
     *  a screen reader, and a second announcement of the same fact is noise. */
    <svg
      className="conv-item__glyph"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
      role="presentation"
    >
      {glyphFor(channel)}
    </svg>
  )
}
